import Lottie from 'lottie-react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type MouseEvent,
} from 'react';
import codAnimation from '@/assets/animations/COD.json';
import slaydTextIcon from '@/assets/svg/static/slaydText.svg';
import { SkeletonLoader } from '@/components/ui';
import { useRazorpayWeb } from '@/hooks/useRazorpayWeb';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import {
  fetchProductAdditionalInfo,
  fetchProductDataById,
} from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useLayoutStore from '@/stores/useLayoutStore';
import type {
  Product,
  ProductReview,
  ProductReviewData,
} from '@/types/Product.types';
import {
  capitalizeFirstLetter,
  decodeBase64Url,
  updatedImageUrl,
} from '@/utils/formatting';
import { isVideoUrl } from '@/utils/mediaUtils';
import { calculateStoreRating } from '@/utils/rating';
import CarouselV2 from './CarouselV2';
import ProductDetailInfo from './ProductDetailInfo';
import { TOAST_DURATION } from './ProductDetailPageV2.constants';
import styles from './ProductDetailPageV2.module.css';
import ProductFooter from './ProductFooter';
import ProductReviewSection from './ProductReviewSection';
import ReelsPlayerOverlay from './ReelsPlayerOverlay';
import SizeSelectionSheet from './SizeSelectionSheet';
import ZoomableCarousel from './ZoomableCarousel';

const ProductDetailPage = (): JSX.Element => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const key = searchParams.get('key');
  let rawInfluencerSlug: string | null = null;
  let rawInstagramHandle: string | null = null;

  if (key) {
    try {
      const decodedStr = decodeBase64Url(key);
      const parts = decodedStr.trim().split('|');
      if (parts[0]) rawInfluencerSlug = parts[0];
      if (parts[1]) rawInstagramHandle = parts[1];
    } catch (err) {
      console.error('Error decoding curator key:', err);
    }
  }

  const influencerSlug = rawInfluencerSlug
    ? capitalizeFirstLetter(rawInfluencerSlug)
    : null;
  const curatorName = influencerSlug || '';
  const instagramHandle = rawInstagramHandle || curatorName;

  useEffect(() => {
    const keyParam = searchParams.get('key');
    const curationIdParam = searchParams.get('curation_id');
    const ridParam = searchParams.get('rid');
    const variantParam = searchParams.get('variant');
    const fbclidParam = searchParams.get('fbclid');

    let parsedCuratorName = '';
    let parsedInstagramName = '';
    let parsedPostId = '';

    if (keyParam) {
      try {
        const decodedStr = decodeBase64Url(keyParam);
        const trimmedDecodedStr = decodedStr.trim();
        const [curator = '', instagram = '', post = ''] =
          trimmedDecodedStr.split('|');
        parsedCuratorName = curator;
        parsedInstagramName = instagram;
        parsedPostId = post;
      } catch (err) {
        console.error('Error decoding key in ProductDetailPageV2:', err);
      }
    }

    trackEvent(EVENTS.PDP_V2_URL_PARAMS, {
      [PROPERTIES.KEY]: keyParam || '',
      [PROPERTIES.CURATION_ID]: curationIdParam || '',
      [PROPERTIES.RID]: ridParam || '',
      [PROPERTIES.VARIANT]: variantParam || '',
      [PROPERTIES.FBCLID]: fbclidParam || '',
      [PROPERTIES.DECODED_CURATOR_NAME]: parsedCuratorName,
      [PROPERTIES.DECODED_INSTAGRAM_NAME]: parsedInstagramName,
      [PROPERTIES.DECODED_POST_ID]: parsedPostId,
    });
  }, [searchParams]);

  const componentKey = id || Date.now();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [productDetails, setProductDetails] = useState<Product>({} as Product);
  const [isProductLoading, setIsProductLoading] = useState<boolean>(true);

  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const [showSizeSheet, setShowSizeSheet] = useState<boolean>(false);

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [activeFullScreenImages, setActiveFullScreenImages] = useState<
    string[]
  >([]);
  const [initialImageIndex, setInitialImageIndex] = useState<number>(0);
  const [activeReviewOverlays, setActiveReviewOverlays] = useState<
    ProductReview[] | undefined
  >(undefined);

  const [reviewsData, setReviewsData] = useState<ProductReviewData | null>(
    null
  );

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [activeReelIndex, setActiveReelIndex] = useState<number | null>(null);

  const productSlaylistOverrides = useSlaylistSelectorStore(
    (state) => state.productSlaylistOverrides
  );

  const productSlaylistOverridesRef = useRef(productSlaylistOverrides);
  productSlaylistOverridesRef.current = productSlaylistOverrides;

  const { initiatePayment, status: paymentStatus } = useRazorpayWeb();
  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  useEffect(() => {
    setNavbarVisibility(false);
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  const triggerToast = (message: string): void => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, TOAST_DURATION);
  };

  const handleTryOnClick = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement | HTMLDivElement> | MouseEvent
    ): void => {
      e.preventDefault();
      e.stopPropagation();

      trackEvent(EVENTS.PDP_V2_TRY_ON_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails?.name || '',
        [PROPERTIES.CURATOR_NAME]: curatorName,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });

      const element = document.getElementById('influencer-picks-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [productDetails?.id, productDetails?.name, curatorName]
  );

  const fallbackCopyTextToClipboard = (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) resolve();
        else reject(new Error('Fallback copy failed'));
      } catch (err) {
        document.body.removeChild(textArea);
        reject(err);
      }
    });
  };

  const handleShareClick = async (_e: MouseEvent): Promise<void> => {
    const url = window.location.href;
    const title = productDetails.name || 'Slayd Originals';
    const text = `Check out this product that I found on Slayd: ${title}`;

    if (window.navigator && window.navigator.share) {
      try {
        await window.navigator.share({
          title,
          text,
          url,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      try {
        if (
          window.navigator &&
          window.navigator.clipboard &&
          window.isSecureContext
        ) {
          await window.navigator.clipboard.writeText(url);
        } else {
          await fallbackCopyTextToClipboard(url);
        }
        triggerToast('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
        triggerToast('Failed to copy link');
      }
    }

    trackEvent(EVENTS.PDP_V2_SHARE_CLICK, {
      [PROPERTIES.PRODUCT_ID]: productDetails.id,
      [PROPERTIES.PRODUCT_NAME]: productDetails.name,
      [PROPERTIES.CURATOR_NAME]: curatorName,
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });
  };

  const handleReelClick = (index: number): void => {
    const video = influencerVideos[index];
    trackEvent(EVENTS.PDP_V2_REEL_CLICK, {
      [PROPERTIES.PRODUCT_ID]: productDetails?.id,
      [PROPERTIES.VIDEO_ID]: video?.id || index,
      [PROPERTIES.CURATOR_NAME]: curatorName,
    });
    setActiveReelIndex(index);
  };

  const fetchProductDetails = useCallback(
    async (options?: { isBackgroundUpdate?: boolean }) => {
      if (!id) return;

      try {
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(true);
        }

        const productData = await fetchProductDataById(id);
        if (productData) {
          if (!productData.isSlaydOriginals) {
            trackEvent(EVENTS.PDP_V2_REDIRECT_TO_V1, {
              [PROPERTIES.PRODUCT_ID]: id,
              [PROPERTIES.CURATOR_NAME]: curatorName,
              [PROPERTIES.CURATION_ID]: searchParams.get('curation_id') || '',
            });
            navigate(`/product/${id}${location.search}`, { replace: true });
            return;
          }

          const transformedProduct: Product = {
            ...productData,
            isSlaydOriginals: productData.isSlaydOriginals || false,
          };

          setProductDetails(transformedProduct);
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
        trackEvent(EVENTS.PDP_V2_LOAD_FAILED, {
          [PROPERTIES.PRODUCT_ID]: id,
          [PROPERTIES.FAILURE_REASON]:
            error instanceof Error ? error.message : String(error),
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
      } finally {
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(false);
        }
      }
    },
    [id, navigate, curatorName, location.search, searchParams]
  );

  useEffect(() => {
    if (!id) return;
    fetchProductAdditionalInfo({ id })
      .then((data) => {
        if (data?.videoUris) {
          data.videoUris = [...data.videoUris].sort((a, b) => {
            const isAVideo = isVideoUrl(a);
            const isBVideo = isVideoUrl(b);
            if (isAVideo && !isBVideo) return -1;
            if (!isAVideo && isBVideo) return 1;
            return 0;
          });
        }
        setReviewsData(data);
      })
      .catch((err) => {
        console.warn(
          'Reviews API failed or not found, falling back to mock high-fidelity review data:',
          err
        );
        trackEvent(EVENTS.REVIEWS_LOAD_FAILED, {
          [PROPERTIES.PRODUCT_ID]: id,
          [PROPERTIES.FAILURE_REASON]:
            err instanceof Error ? err.message : String(err),
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
      });
  }, [id]);

  useEffect(() => {
    if (id) {
      const locationStateProduct = location.state?.productData;
      if (locationStateProduct && locationStateProduct.id.toString() === id) {
        if (!locationStateProduct.isSlaydOriginals) {
          trackEvent(EVENTS.PDP_V2_REDIRECT_TO_V1, {
            [PROPERTIES.PRODUCT_ID]: id,
            [PROPERTIES.CURATOR_NAME]: curatorName,
            [PROPERTIES.CURATION_ID]: searchParams.get('curation_id') || '',
          });
          navigate(`/product/${id}${location.search}`, {
            replace: true,
            state: location.state,
          });
          return;
        }
        const override =
          productSlaylistOverridesRef.current[locationStateProduct.id];
        if (override !== undefined) {
          locationStateProduct.wishlists = override
            ? [{ product_list__name: override.name }]
            : [];
        }
        setProductDetails({
          ...locationStateProduct,
          availableSizes: locationStateProduct.availableSizes || null,
          isSlaydOriginals: locationStateProduct.isSlaydOriginals || false,
        });
        setIsProductLoading(false);
      } else {
        fetchProductDetails();
      }
    }
    setSelectedSize(null);
    setShowSizeSheet(false);
    setIsFullScreen(false);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    id,
    fetchProductDetails,
    navigate,
    location.state,
    location.search,
    curatorName,
    searchParams,
  ]);

  useEffect(() => {
    if (productDetails?.id) {
      const override = productSlaylistOverrides[productDetails.id];
      if (override !== undefined) {
        setProductDetails((prev) => ({
          ...prev,
          wishlists: override
            ? [{ productList_Id: 0, productList_Name: override.name }]
            : [],
        }));
      }
    }
  }, [productSlaylistOverrides, productDetails?.id]);

  useEffect(() => {
    if (productDetails && productDetails.id) {
      trackEvent(EVENTS.PDP_V2_VIEW, {
        [PROPERTIES.PRODUCT_ID]: productDetails.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails.name,
        [PROPERTIES.PRICE]: productDetails.price,
        [PROPERTIES.BRAND]: productDetails.brand,
        [PROPERTIES.IS_AVAILABLE]: productDetails.isAvailable ?? false,
        [PROPERTIES.CURATOR_NAME]: curatorName,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    }
  }, [productDetails, curatorName]);

  const startCheckout = useCallback((): void => {
    const curationId = searchParams.get('curation_id') || undefined;
    initiatePayment(
      productDetails,
      selectedSize || undefined,
      curatorName,
      curationId
    ).catch((err) => {
      console.error(
        '[PDP Buy Button] Checkout payment initiation failed:',
        err
      );
    });
  }, [
    initiatePayment,
    productDetails,
    selectedSize,
    curatorName,
    searchParams,
  ]);

  const handleBuyClick = useCallback(
    async (e: MouseEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      const availableSizes = productDetails.availableSizes;
      const hasSizes =
        availableSizes &&
        (typeof availableSizes === 'string'
          ? availableSizes.trim().length > 0
          : Array.isArray(availableSizes)
            ? (availableSizes as unknown[]).length > 0
            : false);

      trackEvent(EVENTS.PDP_V2_BUY_NOW_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails.id,
        [PROPERTIES.PRODUCT_NAME]: productDetails.name,
        [PROPERTIES.BRAND]: productDetails.brand,
        [PROPERTIES.HAS_SIZES]: !!hasSizes,
        [PROPERTIES.SELECTED_SIZE]: selectedSize || '',
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });

      if (hasSizes) {
        trackEvent(EVENTS.PDP_V2_SIZE_SHEET_OPEN, {
          [PROPERTIES.PRODUCT_ID]: productDetails.id,
          [PROPERTIES.AVAILABLE_SIZES]: availableSizes || '',
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
        setShowSizeSheet(true);
        return;
      }

      startCheckout();
    },
    [productDetails, selectedSize, startCheckout]
  );

  const allImages = useMemo(() => {
    return [
      productDetails.imageUrl,
      ...(productDetails.secondaryImages || []),
    ].filter(Boolean);
  }, [productDetails.imageUrl, productDetails.secondaryImages]);

  const influencerVideos = useMemo(() => {
    if (!reviewsData?.videoUris || reviewsData.videoUris.length === 0) {
      return [];
    }
    return reviewsData.videoUris.map((videoUrl, index) => ({
      id: `reel-${index}`,
      videoUrl: updatedImageUrl(videoUrl),
      thumbnailUrl: productDetails.imageUrl,
      title: `Influencer Pick ${index + 1}`,
    }));
  }, [reviewsData?.videoUris, productDetails.imageUrl]);

  const ratingVal = reviewsData?.overallRating ?? productDetails.rating;
  const ratingCountVal =
    reviewsData?.totalRatings ?? productDetails.ratingCount ?? 0;

  const deliveryDetails = useMemo(() => {
    const deliverySection = productDetails.sections?.find(
      (section) => section.type === 'delivery'
    );
    return deliverySection?.subSections || [];
  }, [productDetails.sections]);

  const storeRating = useMemo(
    () => calculateStoreRating(deliveryDetails),
    [deliveryDetails]
  );

  const handleHeaderClick = (): void => {
    const curationId = searchParams.get('curation_id');
    const key = searchParams.get('key');
    const rid = searchParams.get('rid');
    const variant = searchParams.get('variant');
    const fbclid = searchParams.get('fbclid');

    if (influencerSlug && curationId) {
      const params = new URLSearchParams();
      params.set('curatorName', influencerSlug);
      params.set('curation_id', curationId);
      if (key) params.set('key', key);
      if (rid) params.set('rid', rid);
      if (variant) params.set('variant', variant);
      if (fbclid) params.set('fbclid', fbclid);

      trackEvent(EVENTS.PDP_V2_HEADER_STORE_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.CURATOR_NAME]: curatorName,
        [PROPERTIES.CURATION_ID]: curationId,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        validation_passed: true,
      });

      navigate(`/curator-store?${params.toString()}`);
    } else {
      trackEvent(EVENTS.PDP_V2_HEADER_STORE_CLICK_FAILED, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.CURATOR_NAME]: curatorName || '',
        [PROPERTIES.CURATION_ID]: curationId || '',
        [PROPERTIES.FAILURE_REASON]: 'missing_curator_or_curation_id',
        validation_passed: false,
      });
      navigate('/');
    }
  };

  if (isProductLoading) {
    return <SkeletonLoader.pdp />;
  }

  return (
    <div className={styles.parentProductDetails}>
      <div className={styles.container} key={componentKey} ref={scrollRef}>
        <div className={styles.originalsHeader}>
          {influencerSlug ? (
            <h1
              className={styles.originalsTitle}
              style={{ cursor: 'pointer' }}
              onClick={handleHeaderClick}
            >
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
              <span className={styles.xText}>x</span>
              <span className={styles.originalsTitleCursive}>
                {instagramHandle}
              </span>
            </h1>
          ) : (
            <h1
              className={styles.originalsTitle}
              style={{ cursor: 'pointer' }}
              onClick={handleHeaderClick}
            >
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
            </h1>
          )}
        </div>
        <div className={styles.originalsSubtitleContainer}>
          <span className={styles.originalsSubtitle}>
            Trusted by 1lakh+ customers
          </span>
        </div>

        <div className={styles.gradientWrapper}>
          <CarouselV2
            productDetails={productDetails}
            allImages={allImages}
            ratingVal={Number(ratingVal)}
            ratingCountVal={ratingCountVal}
            storeRating={storeRating}
            videoUris={reviewsData?.videoUris}
            onTryOnClick={handleTryOnClick}
            onImageClick={(index) => {
              setInitialImageIndex(index);
              setActiveFullScreenImages(allImages.map(updatedImageUrl));
              setActiveReviewOverlays(undefined);
              setIsFullScreen(true);
            }}
          />

          <div className={styles.codBanner}>
            <Lottie
              autoplay
              animationData={codAnimation}
              className={styles.codLottie}
              loop={true}
            />
          </div>
        </div>

        <ProductDetailInfo
          productDetails={productDetails}
          curatorName={instagramHandle}
          selectedSize={selectedSize}
          description={productDetails.description || ''}
          influencerVideos={influencerVideos}
          paymentStatus={paymentStatus}
          onSizeSelect={(size) => {
            setSelectedSize(size);
            setShowSizeSheet(true);
          }}
          onViewSizeChart={() => {
            if (productDetails.sizeChartUrl) {
              setInitialImageIndex(0);
              setActiveFullScreenImages([
                updatedImageUrl(productDetails.sizeChartUrl),
              ]);
              setActiveReviewOverlays(undefined);
              setIsFullScreen(true);
            } else {
              triggerToast('No size chart available for this product');
            }
          }}
          onReelClick={handleReelClick}
          onShareClick={handleShareClick}
          onBuyClick={handleBuyClick}
        />

        <ProductReviewSection
          reviewsData={reviewsData}
          ratingVal={Number(ratingVal)}
          ratingCountVal={ratingCountVal}
          productDetails={productDetails}
          allImages={allImages}
          onReviewImageClick={(images, index, overlays) => {
            setInitialImageIndex(index);
            setActiveFullScreenImages(images);
            setActiveReviewOverlays(overlays);
            setIsFullScreen(true);
          }}
        />

        <ProductFooter
          curatorName={instagramHandle}
          productDetails={productDetails}
        />

        {toastMessage && <div className={styles.toastWrap}>{toastMessage}</div>}

        {showSizeSheet && (
          <SizeSelectionSheet
            availableSizes={productDetails.availableSizes}
            selectedSize={selectedSize}
            paymentStatus={paymentStatus}
            sizeChartUrl={productDetails.sizeChartUrl}
            onSizeSelect={(size) => {
              trackEvent(EVENTS.PDP_V2_SIZE_SELECTED, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.SIZE]: size,
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });
              setSelectedSize(size);
            }}
            onClose={() => {
              trackEvent(EVENTS.PDP_V2_SIZE_SHEET_CLOSE, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });
              setShowSizeSheet(false);
            }}
            onContinue={() => {
              trackEvent(EVENTS.PDP_V2_SIZE_SHEET_CONTINUE, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.SELECTED_SIZE]: selectedSize || '',
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });
              setShowSizeSheet(false);
              startCheckout();
            }}
          />
        )}

        {isFullScreen && (
          <ZoomableCarousel
            images={activeFullScreenImages}
            initialIndex={initialImageIndex}
            reviewOverlays={activeReviewOverlays}
            productId={productDetails.id}
            onClose={() => setIsFullScreen(false)}
          />
        )}

        {activeReelIndex !== null && (
          <ReelsPlayerOverlay
            videos={influencerVideos}
            initialIndex={activeReelIndex}
            productId={productDetails.id}
            onClose={() => setActiveReelIndex(null)}
          />
        )}
      </div>
    </div>
  );
};
export default ProductDetailPage;
